import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Save, Search, Eye, AlertTriangle, RotateCcw,
  Lock, CheckCircle, X,
} from "lucide-react";

const SENSITIVE_ROLES = new Set(["admin", "jefe_taller", "rrhh"]);

function Cell({ active, locked, onToggle, label, testid }) {
  return (
    <label
      title={label}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-sm border text-[10px] uppercase tracking-wider cursor-pointer transition-colors ${
        active
          ? "bg-emerald-700 text-white border-emerald-700"
          : "bg-white text-slate-400 border-slate-300 hover:border-slate-500"
      } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
      data-testid={testid}
    >
      <input
        type="checkbox"
        className="hidden"
        checked={active}
        disabled={locked}
        onChange={onToggle}
      />
      {active ? "✓" : ""}
    </label>
  );
}

export default function PermissionsMatrixPage() {
  const { axiosAuth, user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({}); // roleId -> {moduleKey -> [actions]}
  const [filter, setFilter] = useState("");
  const [savingRole, setSavingRole] = useState(null);

  // Doble confirmación
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  // Restaurar base
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreText, setRestoreText] = useState("");
  const [restoring, setRestoring] = useState(false);

  // Ver como rol
  const [previewSlug, setPreviewSlug] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/admin/permissions-matrix");
      setMatrix(res.data);
      // Inicializar borrador con permisos actuales
      const d = {};
      res.data.roles.forEach((r) => {
        d[r.id] = { ...(r.permissions || {}) };
      });
      setDraft(d);
    } catch (err) {
      toast.error("Error cargando matriz");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatrix(); /* eslint-disable-next-line */ }, []);

  // Agrupación de módulos por categoría
  const grouped = useMemo(() => {
    if (!matrix) return [];
    const q = filter.trim().toLowerCase();
    const filterFn = (m) => {
      if (!q) return true;
      return (
        m.key.toLowerCase().includes(q) ||
        m.label.toLowerCase().includes(q) ||
        (m.group || "").toLowerCase().includes(q)
      );
    };
    const map = new Map();
    matrix.modules.filter(filterFn).forEach((m) => {
      const g = m.group || "General";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(m);
    });
    return Array.from(map.entries());
  }, [matrix, filter]);

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="permissions-forbidden">
        <Card className="rounded-sm p-6 border border-amber-200 bg-amber-50">
          <AlertTriangle className="w-6 h-6 text-amber-700 inline mr-2" />
          <span className="text-sm text-amber-900">Esta página requiere rol super_admin o admin.</span>
        </Card>
      </div>
    );
  }
  if (loading || !matrix) return <div className="p-8 text-xs text-slate-500" data-testid="permissions-loading">Cargando matriz…</div>;

  const hasLegacy = Object.keys(matrix.legacy_modules_detected || {}).length > 0;
  const hasUnknown = Object.keys(matrix.unknown_modules_detected || {}).length > 0;

  const toggle = (roleId, moduleKey, action, allowedActions) => {
    if (!editing) return;
    setDraft((prev) => {
      const next = { ...prev };
      const rolePerms = { ...(next[roleId] || {}) };
      const cur = new Set(rolePerms[moduleKey] || []);
      if (cur.has(action)) cur.delete(action);
      else cur.add(action);
      if (cur.size === 0) {
        delete rolePerms[moduleKey];
      } else {
        rolePerms[moduleKey] = allowedActions.filter((a) => cur.has(a));
      }
      next[roleId] = rolePerms;
      return next;
    });
  };

  const askSaveRole = (role) => {
    if (SENSITIVE_ROLES.has(role.slug)) {
      setConfirmText("");
      setConfirmDialog({ role, mode: "save" });
    } else {
      saveRole(role, null);
    }
  };

  const saveRole = async (role, confirmValue) => {
    setSavingRole(role.id);
    try {
      const res = await axiosAuth().put(
        `/admin/permissions-matrix/role/${role.id}`,
        { permissions: draft[role.id] || {}, confirm: confirmValue || undefined },
      );
      const added = res.data.diff_added || {};
      const removed = res.data.diff_removed || {};
      const addedCount = Object.values(added).reduce((s, a) => s + a.length, 0);
      const removedCount = Object.values(removed).reduce((s, a) => s + a.length, 0);
      toast.success(
        `Permisos del rol '${role.name}' guardados (${addedCount} añadidos, ${removedCount} eliminados)`,
      );
      setConfirmDialog(null);
      setConfirmText("");
      fetchMatrix();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "confirmation_required") {
        toast.error(d.message);
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Error al guardar"));
      }
    } finally {
      setSavingRole(null);
    }
  };

  const restoreBase = async () => {
    setRestoring(true);
    try {
      await axiosAuth().post("/admin/permissions-matrix/restore-base", {
        confirm: restoreText,
      });
      toast.success("Permisos base restaurados");
      setRestoreOpen(false);
      setRestoreText("");
      fetchMatrix();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Error al restaurar"));
    } finally {
      setRestoring(false);
    }
  };

  const openPreview = async (slug) => {
    setPreviewSlug(slug);
    setPreviewData(null);
    try {
      const res = await axiosAuth().get(`/admin/permissions-matrix/preview-as/${slug}`);
      setPreviewData(res.data);
    } catch (err) {
      toast.error("Error al simular vista de rol");
    }
  };

  return (
    <div className="space-y-3" data-testid="permissions-matrix-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-industrial text-2xl uppercase tracking-wide">
            <ShieldCheck className="w-5 h-5 inline mr-2 mb-1" /> Permisos y roles
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Matriz de qué puede ver y hacer cada rol. {isSuperAdmin
              ? "Como super_admin puedes editar los permisos."
              : "Solo super_admin puede modificar permisos (admin tiene visión de solo lectura)."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Filtrar módulos…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-sm h-8 text-xs pl-7 w-44"
              data-testid="permissions-filter"
            />
          </div>
          {isSuperAdmin && (
            <>
              <Button
                variant={editing ? "default" : "outline"}
                onClick={() => setEditing(!editing)}
                className="rounded-sm text-xs uppercase"
                data-testid="permissions-edit-toggle"
              >
                {editing ? <CheckCircle className="w-3.5 h-3.5 mr-1" /> : <Lock className="w-3.5 h-3.5 mr-1" />}
                {editing ? "Modo edición activo" : "Activar edición"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRestoreOpen(true)}
                className="rounded-sm text-xs uppercase"
                data-testid="permissions-restore-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurar base
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Avisos de permisos legacy / desconocidos */}
      {(hasLegacy || hasUnknown) && (
        <div className="space-y-2" data-testid="permissions-legacy-warning">
          {hasLegacy && (
            <Card className="rounded-sm border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-900">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                Se han detectado permisos antiguos en algunos roles. Serán migrados automáticamente al nuevo catálogo al guardar.
              </p>
              <ul className="text-[11px] text-amber-800 mt-1.5 ml-5 list-disc">
                {Object.entries(matrix.legacy_modules_detected).map(([slug, mappings]) => (
                  <li key={slug}>
                    <strong>{slug}</strong>: {mappings.join(", ")}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {hasUnknown && (
            <Card className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid="permissions-unknown-warning">
              <p className="text-xs text-red-900">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                Se han detectado módulos no catalogados (ignorados al guardar):
              </p>
              <ul className="text-[11px] text-red-800 mt-1.5 ml-5 list-disc">
                {Object.entries(matrix.unknown_modules_detected).map(([slug, mods]) => (
                  <li key={slug}><strong>{slug}</strong>: {mods.join(", ")}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {/* Cabecera con roles + acción "Ver como rol" + guardar por rol */}
      <Card className="rounded-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 border-b border-slate-200 min-w-[260px] sticky left-0 bg-slate-50 z-20">
                  Módulo · Acción
                </th>
                {matrix.roles.map((r) => (
                  <th key={r.id} className="p-2 border-b border-l border-slate-200 align-bottom min-w-[140px]">
                    <div className="font-semibold text-slate-700 break-words">{r.name}</div>
                    <div className="text-[10px] font-mono text-slate-400">{r.slug}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <button
                        onClick={() => openPreview(r.slug)}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-300 hover:bg-slate-100 inline-flex items-center"
                        data-testid={`permissions-view-as-${r.slug}`}
                      >
                        <Eye className="w-3 h-3 mr-0.5" /> Ver
                      </button>
                      {editing && !r.is_super_admin && (
                        <button
                          onClick={() => askSaveRole(r)}
                          disabled={savingRole === r.id}
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center disabled:opacity-60"
                          data-testid={`permissions-save-${r.slug}`}
                        >
                          <Save className="w-3 h-3 mr-0.5" /> {savingRole === r.id ? "…" : "Guardar"}
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="permissions-matrix-body">
              {grouped.map(([groupName, mods]) => (
                <>
                  <tr key={`g-${groupName}`} className="bg-slate-100">
                    <td className="p-1.5 px-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold sticky left-0 bg-slate-100 z-10" colSpan={1 + matrix.roles.length}>
                      {groupName}
                    </td>
                  </tr>
                  {mods.map((m) => {
                    const actions = m.actions && m.actions.length ? m.actions : matrix.default_actions;
                    return (
                      <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="p-2 align-top sticky left-0 bg-white" data-testid={`permissions-row-${m.key}`}>
                          <div className="font-semibold text-slate-700 text-xs">{m.label}</div>
                          <div className="text-[10px] font-mono text-slate-400">{m.key}</div>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {actions.map((a) => (
                              <span key={a} className="text-[9px] uppercase tracking-wider bg-slate-100 border border-slate-200 px-1 py-0.5 rounded-sm">
                                {a}
                              </span>
                            ))}
                          </div>
                        </td>
                        {matrix.roles.map((r) => {
                          const rolePerms = draft[r.id] || {};
                          const granted = new Set(rolePerms[m.key] || []);
                          const locked = !editing || r.is_super_admin;
                          return (
                            <td
                              key={r.id}
                              className={`p-2 border-l border-slate-100 align-top ${r.is_super_admin ? "bg-amber-50/40" : ""}`}
                            >
                              {r.is_super_admin ? (
                                <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                                  Total
                                </span>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {actions.map((a) => (
                                    <Cell
                                      key={a}
                                      active={granted.has(a)}
                                      locked={locked}
                                      label={`${a} en ${m.label}`}
                                      onToggle={() => toggle(r.id, m.key, a, actions)}
                                      testid={`permissions-cell-${r.slug}-${m.key}-${a}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Doble confirmación para roles sensibles */}
      <Dialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="permissions-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base uppercase">
              <AlertTriangle className="w-4 h-4 inline mr-1.5 text-amber-600" />
              Cambio sensible
            </DialogTitle>
            <DialogDescription className="text-xs">
              Estás a punto de modificar los permisos del rol{" "}
              <strong>{confirmDialog?.role?.name}</strong> ({confirmDialog?.role?.slug}).
              Para continuar, escribe <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">CONFIRMO_CAMBIOS_PERMISOS</code> en el campo de abajo.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="rounded-sm h-9 text-xs font-mono"
            placeholder="CONFIRMO_CAMBIOS_PERMISOS"
            data-testid="permissions-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)} className="rounded-sm text-xs uppercase">
              Cancelar
            </Button>
            <Button
              onClick={() => saveRole(confirmDialog.role, confirmText)}
              disabled={confirmText !== "CONFIRMO_CAMBIOS_PERMISOS" || savingRole}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm text-xs uppercase font-semibold disabled:bg-slate-300"
              data-testid="permissions-confirm-submit"
            >
              Confirmar y guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restaurar base */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="permissions-restore-dialog">
          <DialogHeader>
            <DialogTitle className="text-base uppercase">
              <RotateCcw className="w-4 h-4 inline mr-1.5" /> Restaurar permisos base
            </DialogTitle>
            <DialogDescription className="text-xs">
              Esto restablece los permisos por defecto de los roles del sistema (admin, jefe_taller, jefe_recambios, rrhh, recepcion, empleado). Los cambios manuales se perderán.
              Para continuar escribe <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">RESTAURAR_PERMISOS_BASE</code>.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={restoreText}
            onChange={(e) => setRestoreText(e.target.value)}
            className="rounded-sm h-9 text-xs font-mono"
            placeholder="RESTAURAR_PERMISOS_BASE"
            data-testid="permissions-restore-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} className="rounded-sm text-xs uppercase">
              Cancelar
            </Button>
            <Button
              onClick={restoreBase}
              disabled={restoreText !== "RESTAURAR_PERMISOS_BASE" || restoring}
              className="bg-amber-700 hover:bg-amber-800 text-white rounded-sm text-xs uppercase font-semibold disabled:bg-slate-300"
              data-testid="permissions-restore-submit"
            >
              {restoring ? "Restaurando…" : "Restaurar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ver como rol */}
      <Dialog open={!!previewSlug} onOpenChange={(o) => { if (!o) { setPreviewSlug(null); setPreviewData(null); } }}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="permissions-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base uppercase">
              <Eye className="w-4 h-4 inline mr-1.5" /> Ver como rol: {previewSlug}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Vista simulada de los módulos accesibles y las acciones efectivas para este rol.
            </DialogDescription>
          </DialogHeader>
          {!previewData ? (
            <p className="text-xs text-slate-400 text-center py-4">Cargando…</p>
          ) : (
            <div className="space-y-3 text-xs">
              {previewData.is_super_admin && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-900">
                  Este rol tiene <strong>acceso total</strong> a todos los módulos por diseño.
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Módulos accesibles ({previewData.accessible_modules.length})
                </p>
                {previewData.accessible_modules.length === 0 ? (
                  <p className="text-slate-400 text-xs italic">Ningún módulo accesible.</p>
                ) : (
                  <ul className="space-y-1">
                    {previewData.accessible_modules.map((m) => (
                      <li key={m.key || m} className="border border-slate-200 rounded-sm p-1.5 flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-700">{m.label || m}</span>
                        <span className="text-[10px] font-mono text-slate-400">({m.key || m})</span>
                        {(m.actions || []).map((a) => (
                          <span key={a} className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm">{a}</span>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {previewData.inaccessible_modules.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    Módulos SIN acceso ({previewData.inaccessible_modules.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {previewData.inaccessible_modules.map((m) => (
                      <span key={m.key} className="text-[10px] bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm text-slate-500">
                        {m.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewSlug(null); setPreviewData(null); }} className="rounded-sm text-xs uppercase">
              <X className="w-3.5 h-3.5 mr-1" /> Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
